import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AccountUnit } from '../../../../models/api/alerts/account-unit';
import { Api } from '../../../../services/api/api.service';
import { ButtonComponent } from '../../buttons/button.component';
import { ActionRowComponent } from '../../panels/action-row.component';
import { DialogComponent } from '../../panels/dialog.component';
import { LoadingPanelComponent } from '../../panels/loading-panel.component';
import { InputComponent } from '../input.component';
import { RadioButtonComponent } from '../radio-button.component';
import { BaseControlComponent } from './../base-control.component';
import { HelpTextComponent } from './../help-text.component';
import { AccountsComponent } from './accounts.component';

@Component({
  selector: 'app-account-selector',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    HelpTextComponent,
    ButtonComponent,
    ActionRowComponent,
    RadioButtonComponent,
    AccountsComponent,
    DialogComponent,
    InputComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-action-row>
      <app-button
        label="Add account"
        left1
        emphasis="Low"
        icon="fa-light fa-plus"
        (click)="openSearchDialog()"
      ></app-button>
      <div right1 class="tw-flex tw-items-center">
        <app-radio-button
          [control]="includeControl"
          optionLabel="name"
          optionValue="code"
          [options]="includeOptions"
          [showHelpText]="false"
          [vertical]="false"
        ></app-radio-button>
      </div>
    </app-action-row>

    <app-accounts
      [items]="selectedAccounts"
      [showDeleteColumn]="true"
      (itemsChanged)="onSelectedAccountsChanged($event)"
    />

    @if (showHelpText) {
      <app-help-text [control]="control" [helpText]="helpText" />
    }

    <app-dialog
      #searchDialog
      [visible]="dialogVisible"
      [closable]="false"
      label="Add accounts and units"
      [width]="'900px'"
    >
      <ng-template #content>
        <form [formGroup]="searchForm" class="tw-pt-2">
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
            <app-input
              [control]="searchForm.controls.accountNumberName"
              label="Account Number/Name"
            />
            <app-input
              [control]="searchForm.controls.unitNumberName"
              label="Unit Number/Name"
            />
          </div>
        </form>

        <form [formGroup]="searchForm" class="tw-pt-4">
          <div
            class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4"
          >
            <app-input
              [control]="searchForm.controls.address"
              label="Address"
              helpText="Address only applies to unit"
            />
            <app-input [control]="searchForm.controls.city" label="City" />
            <app-input [control]="searchForm.controls.state" label="State" />
            <app-input [control]="searchForm.controls.zip" label="Zip Code" />
          </div>
        </form>

        <app-action-row useMobileLayout>
          <app-button
            left1
            type="submit"
            label="Search"
            [loading]="searching"
            (click)="search()"
          />
          <app-button
            left2
            emphasis="Low"
            label="Clear"
            (click)="clearSearch()"
          />
        </app-action-row>

        <app-loading-panel
          loadingStyle="Box"
          [state]="searching ? 'Loading' : 'Loaded'"
        >
          <ng-template #loaded>
            <app-accounts
              #searchResultsTable
              [items]="searchResults"
              [showDeleteColumn]="false"
              paginatorDropdownAppendTo="self"
              (selected)="onSearchSelectionChanged($event)"
            />
          </ng-template>
        </app-loading-panel>

        <app-action-row useMobileLayout>
          <app-button
            [disabled]="!hasSearchSelection"
            [badgeLabel]="searchSelectedCount ? searchSelectedCount : null"
            right1
            label="Add"
            icon="fa-light fa-plus"
            (click)="addSelectedAccounts()"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeDialog()"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
  `,
  styles: ``,
})
export class AccountSelectorComponent
  extends BaseControlComponent
  implements OnInit
{
  @ViewChild('searchResultsTable') searchResultsTable: AccountsComponent;

  @Input()
  contractNumber = 0;

  includeControl = new FormControl<string>('Include');

  searchForm = new FormGroup({
    accountNumberName: new FormControl<string>(null),
    unitNumberName: new FormControl<string>(null),
    address: new FormControl<string>(null),
    city: new FormControl<string>(null),
    state: new FormControl<string>(null),
    zip: new FormControl<string>(null),
  });

  includeOptions = [
    { name: 'Include', code: 'Include' },
    { name: 'Exclude', code: 'Exclude' },
  ];

  searchResults: AccountUnit[] = [];
  selectedAccounts: AccountUnit[] = [];
  searchSelectedItems: AccountUnit[] = [];
  dialogVisible = false;
  searching = false;
  hasSearchSelection = false;
  searchSelectedCount = 0;

  private api: Api;

  constructor() {
    const api = inject(Api);

    super();
    this.api = api;
  }

  ngOnInit() {
    if (this.control?.value) {
      const value = this.control.value as {
        includeType: string;
        accounts: AccountUnit[];
      };

      if (value.includeType) {
        this.includeControl.setValue(value.includeType);
      }

      if (value.accounts) {
        this.selectedAccounts = [...value.accounts];
      }
    }

    this.includeControl.valueChanges.subscribe(() => {
      this.updateControlValue();
    });
  }

  openSearchDialog() {
    this.dialogVisible = true;
    this.searchResults = [];
    this.hasSearchSelection = false;
    this.searchSelectedCount = 0;
    this.clearSearch();
  }

  closeDialog() {
    this.dialogVisible = false;
    this.searchResults = [];
    this.hasSearchSelection = false;
    this.searchSelectedCount = 0;

    if (this.searchResultsTable) {
      this.searchResultsTable.clearSelection();
    }
  }

  clearSearch() {
    this.searchForm.reset();
    this.searchResults = [];
    this.hasSearchSelection = false;
    this.searchSelectedCount = 0;

    if (this.searchResultsTable) {
      this.searchResultsTable.clearSelection();
    }
  }

  async search() {
    if (this.contractNumber <= 0) {
      return;
    }

    this.searching = true;
    this.hasSearchSelection = false;

    try {
      const response = await this.api.newAlert.searchAccountUnits({
        contractNumber: this.contractNumber,
        accountSearch:
          this.searchForm.controls.accountNumberName.value || undefined,
        unitSearch: this.searchForm.controls.unitNumberName.value || undefined,
        address: this.searchForm.controls.address.value || undefined,
        city: this.searchForm.controls.city.value || undefined,
        state: this.searchForm.controls.state.value || undefined,
        zip: this.searchForm.controls.zip.value || undefined,
      });

      this.searchResults = response.accountUnits || [];

      const selectedIds = new Set(this.selectedAccounts.map((a) => a.id));
      this.searchResults = this.searchResults.filter(
        (r) => !selectedIds.has(r.id),
      );
    } catch {
      this.searchResults = [];
    } finally {
      this.searching = false;
    }
  }

  onSearchSelectionChanged(hasSelection: boolean) {
    this.hasSearchSelection = hasSelection;
    if (this.searchResultsTable && hasSelection) {
      this.searchSelectedItems = this.searchResultsTable.getSelectedItems();
      this.searchSelectedCount = this.searchSelectedItems.length;
    } else {
      this.searchSelectedItems = [];
      this.searchSelectedCount = 0;
    }
  }

  addSelectedAccounts() {
    if (!this.searchResultsTable) {
      return;
    }

    const selected = this.searchResultsTable.getSelectedItems();
    if (selected.length === 0) {
      return;
    }

    const existingIds = new Set(this.selectedAccounts.map((a) => a.id));

    for (const item of selected) {
      if (!existingIds.has(item.id)) {
        this.selectedAccounts.push({ ...item });
        existingIds.add(item.id);
      }
    }

    this.selectedAccounts = [...this.selectedAccounts];

    const addedIds = new Set(selected.map((a) => a.id));
    this.searchResults = this.searchResults.filter((r) => !addedIds.has(r.id));

    this.searchSelectedItems = [];
    this.hasSearchSelection = false;
    this.searchSelectedCount = 0;

    this.searchResultsTable.clearSelection();

    this.updateControlValue();
  }

  onSelectedAccountsChanged(accounts: AccountUnit[]) {
    this.selectedAccounts = accounts;
    this.updateControlValue();
  }

  private updateControlValue() {
    if (this.control) {
      this.control.setValue({
        includeType: this.includeControl.value,
        accounts: this.selectedAccounts,
      });
    }
  }

  getSelectedAccounts(): AccountUnit[] {
    return this.selectedAccounts;
  }
}
